import { createSelector } from 'reselect';
import {
  data as dataSelector,
  data2 as data2Selector,
} from '../../../AdvWorkflow/AdvWorkflowSelector';
import { BACKEND_KEY } from '../../AccountsWorkflowConstants';
import { isNumeric, isEmpty } from '../../../../../js/app/react/validators';

export const MORTGAGE_ACCOUNT_TYPE = 13;
export const STUDENT_LOAN_ACCOUNT_TYPE = 16;
const CREDIT_CARD_ACCOUNT_TYPE = 8;

const doesAccountTypeSupportTrustOwner = (selectedAccountType, selectedInsuranceType) => {
  const SEPIRA = 17;
  const SIMPLEIRA = 19;
  const EmployerPlan = 27;
  const T401K = 4;
  const Roth401K = 22;
  const T401A = 23;
  const T403B = 10;
  const T457 = 11;
  const TaxFree = 28;
  const INSURANCE = 20;

  const accountsWithNoTrust = [
    SEPIRA,
    SIMPLEIRA,
    EmployerPlan,
    T401K,
    Roth401K,
    T401A,
    T403B,
    T457,
    TaxFree,
  ];

  if (selectedAccountType === INSURANCE) {
    const LIFE_INSURANCE = 0;

    return selectedInsuranceType === LIFE_INSURANCE;
  }

  return accountsWithNoTrust.indexOf(selectedAccountType) < 0;
};

const doesAccountTypeSupportJointOwner = currentAccountTypeSelected => {
  const checkingAccountType = 0;
  const CDAccountType = 2;
  const savingsAccountType = 1;
  const brokerageAccountType = 3;
  const creditCardAccountType = 8;
  const installementAccountType = 14;
  const lineOfCreditAccountType = 15;
  const autoLoanType = 29;

  return (
    [
      checkingAccountType,
      CDAccountType,
      savingsAccountType,
      brokerageAccountType,
      creditCardAccountType,
      installementAccountType,
      lineOfCreditAccountType,
      autoLoanType,
    ].indexOf(currentAccountTypeSelected) >= 0
  );
};

export const accountDraftSelector = createSelector(
  data2Selector,
  data2 => data2(`${BACKEND_KEY}.accountDraft`) || {},
);

export const accountTypesSelector = createSelector(
  dataSelector,
  data => data('DebtAccountTypes') || [],
);

export const mortgageTypesOptionsSelector = createSelector(
  dataSelector,
  data => data('MortgageTypeOptions') || [],
);

export const accountOwnersSelector = createSelector(
  dataSelector,
  data => data('AccountOwners') || [],
);

export const accountTypesOptionsSelector = createSelector(accountTypesSelector, accountTypes =>
  accountTypes.map(accountType => ({
    groupId: accountType.GroupId,
    label: accountType.Label,
    value: accountType.AccountType,
    extra: accountType,
  })),
);

export const accountOwnersOptionsSelector = createSelector(
  accountOwnersSelector,
  accountDraftSelector,
  (accountOwners, accountDraft) =>
    Object.keys(accountOwners).reduce(
      (accumulator, groupKey) =>
        accumulator
          .concat(
            (accountOwners[groupKey] || []).map(accountOwner => ({
              label: accountOwner.Name,
              value: accountOwner.Id,
              groupId: groupKey.toLowerCase(),
            })),
          )
          .filter(({ value, groupId }) => {
            if (value === 'Joint') {
              return doesAccountTypeSupportJointOwner(accountDraft.AccountType);
            }

            if (groupId === 'trusts') {
              return doesAccountTypeSupportTrustOwner(accountDraft.AccountType);
            }

            return true;
          }),
      [],
    ),
);

export const accountTypesGroupsSelector = createSelector(
  dataSelector,
  data => data('AccountTypesGroups') || [],
);

export const nullableAccountTypeOptionSelector = createSelector(
  accountTypesOptionsSelector,
  accountDraftSelector,
  (accountTypesOptions, accountDraft) =>
    accountTypesOptions.find(option => option.value === accountDraft.AccountType) || null,
);

export const nullableAccountOwnerOptionSelector = createSelector(
  accountOwnersOptionsSelector,
  accountDraftSelector,
  (accountOwnersOptions, accountDraft) =>
    accountOwnersOptions.find(option => option.value === accountDraft.AccountOwner) || null,
);

export const nullableMortgageTypeOptionSelector = createSelector(
  mortgageTypesOptionsSelector,
  accountDraftSelector,
  (mortgageTypeOptions, accountDraft) =>
    mortgageTypeOptions.find(option => option.value === accountDraft.MortgageType) || null,
);

export const accountOwnersGroupsSelector = createSelector(accountOwnersSelector, accountOwners =>
  Object.keys(accountOwners).map(groupKey => ({
    groupId: groupKey.toLowerCase(),
    title: groupKey,
  })),
);

export const groupsSelector = createSelector(accountTypesGroupsSelector, accountTypesGroups =>
  Object.keys(accountTypesGroups).map(key => ({
    groupId: key,
    title: accountTypesGroups[key],
  })),
);

export const nullableAccountTypeSelector = createSelector(
  nullableAccountTypeOptionSelector,
  nullableAccountTypeOption =>
    !isEmpty(nullableAccountTypeOption) && isNumeric(nullableAccountTypeOption.value)
      ? +nullableAccountTypeOption.value
      : null,
);

export const interestRateLabelSelector = createSelector(
  nullableAccountTypeSelector,
  nullableAccountType => {
    switch (nullableAccountType) {
      case CREDIT_CARD_ACCOUNT_TYPE:
        return 'APR';
      default:
        return 'Interest Rate';
    }
  },
);

export const isMortgageAccountSelector = createSelector(
  nullableAccountTypeSelector,
  nullableAccountType => {
    switch (nullableAccountType) {
      case MORTGAGE_ACCOUNT_TYPE:
        return true;
      default:
        return false;
    }
  },
);

export const isStudentLoanAccountSelector = createSelector(
  nullableAccountTypeSelector,
  nullableAccountType => {
    switch (nullableAccountType) {
      case STUDENT_LOAN_ACCOUNT_TYPE:
        return true;
      default:
        return false;
    }
  },
);

export const ownerModelSelector = createSelector(accountDraftSelector, accountDraft => {
  if (!accountDraft) {
    return null;
  }

  return accountDraft.OwnerModel;
});
